import { useState, useEffect, useRef, useMemo } from 'react';
import { Pause, PlayCircle, SkipForward, StopCircle, Terminal, ChevronRight, CheckCircle, XCircle, Clock, Zap, Globe, Database, Search, X, Trophy, AlertTriangle, Workflow } from 'lucide-react';

// ─── Generic pipeline step definitions for non-preview mode ───
const PIPELINE_STEPS = [
    { id: 'init',    icon: '1', label: 'Preparing record',                desc: 'Loading the record and pipeline settings' },
    { id: 'step0',   icon: '2', label: 'Checking Master DB',              desc: 'Looking for an exact part-number match' },
    { id: 'step1',   icon: '3', label: 'Searching the web',               desc: 'Searching manufacturer and supplier sources' },
    { id: 'step1.5', icon: '4', label: 'Matching search results',         desc: 'Selecting results that match this product' },
    { id: 'step2',   icon: '5', label: 'Selecting the best source',       desc: 'Classifying sources and choosing the best URL' },
    { id: 'step2.5', icon: '6', label: 'Validating the source',           desc: 'Confirming manufacturer and source authenticity' },
    { id: 'step3',   icon: '7', label: 'Scraping product data',           desc: 'Reading product information from the selected source' },
    { id: 'step4',   icon: '8', label: 'Extracting specifications',       desc: 'Finding technical specifications in the source data' },
    { id: 'step4.5', icon: '9', label: 'Cleaning extracted data',         desc: 'Removing noise and normalizing specifications' },
    { id: 'step6',   icon: '10', label: 'Mapping attributes',             desc: 'Mapping values to the requested INC properties' },
    { id: 'save',    icon: '11', label: 'Saving the result',              desc: 'Saving the completed record' },
];

// Map currentStepName to a pipeline step index
function getActiveStepIndex(stepName) {
    if (!stepName) return 0;
    const lower = stepName.toLowerCase();
    if (lower.includes('step 0') || lower.includes('db check'))          return 1;
    if (lower.includes('step 1') && lower.includes('web search'))        return 2;
    if (lower.includes('step 1.5') || lower.includes('matching'))        return 3;
    if (lower.includes('step 2') && !lower.includes('2.5'))              return 4;
    if (lower.includes('step 2.5') || lower.includes('validation'))      return 5;
    if (lower.includes('step 3') || lower.includes('scrap'))             return 6;
    if (lower.includes('step 4') && !lower.includes('4.5'))              return 7;
    if (lower.includes('step 4.5') || lower.includes('cleaning'))        return 8;
    if (lower.includes('step 6') || lower.includes('attribute'))         return 9;
    if (lower.includes('sav'))                                           return 10;
    return 0;
}

/**
 * ProcessingOverlay — Full-screen glassmorphism overlay shown during pipeline processing.
 */
export default function ProcessingOverlay({
    visible,
    pipelineState,       // 'running' | 'paused' | 'completed' | 'stopped'
    enrichmentStats,     // { total, completed, failed, current }
    enrichmentLogs,
    stepReview,
    startTime,           // Date.now() when processing started
    currentStepName,     // e.g. "Step 3: Web Scraping"
    previewMode,         // true = show detailed console, false = show generic steps
    onPause,
    onResume,
    onStop,
    onSkip,
    onClose,
    consoleEndRef,
    renderStepData,
    handleApprove,
    handleSkipRow,
}) {
    const [dots, setDots] = useState('');
    const [particleKey, setParticleKey] = useState(0);
    const overlayConsoleRef = useRef(null);

    const isActive = pipelineState === 'running' || pipelineState === 'paused';
    const isFinished = pipelineState === 'completed' || pipelineState === 'stopped';
    const activeStepIdx = getActiveStepIndex(currentStepName);

    // Animate dots
    useEffect(() => {
        if (!visible || !isActive) return;
        const iv = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 400);
        return () => clearInterval(iv);
    }, [visible, isActive]);

    // Refresh particles
    useEffect(() => {
        if (!visible || !isActive) return;
        const iv = setInterval(() => setParticleKey(k => k + 1), 8000);
        return () => clearInterval(iv);
    }, [visible, isActive]);

    // Auto-scroll console
    useEffect(() => {
        if (overlayConsoleRef.current) {
            overlayConsoleRef.current.scrollTop = overlayConsoleRef.current.scrollHeight;
        }
    }, [enrichmentLogs]);

    // ETA calculation
    const eta = useMemo(() => {
        if (!startTime || !enrichmentStats.total) return null;
        const processed = enrichmentStats.completed + enrichmentStats.failed;
        if (processed === 0) return 'Calculating...';
        const elapsed = (Date.now() - startTime) / 1000;
        const perRecord = elapsed / processed;
        const remaining = enrichmentStats.total - processed;
        const etaSeconds = Math.round(perRecord * remaining);
        if (etaSeconds < 60) return `~${etaSeconds}s`;
        if (etaSeconds < 3600) return `~${Math.floor(etaSeconds / 60)}m ${etaSeconds % 60}s`;
        return `~${Math.floor(etaSeconds / 3600)}h ${Math.floor((etaSeconds % 3600) / 60)}m`;
    }, [startTime, enrichmentStats]);

    // Elapsed time
    const elapsed = useMemo(() => {
        if (!startTime) return '—';
        const secs = Math.round((Date.now() - startTime) / 1000);
        if (secs < 60) return `${secs}s`;
        if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
        return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
    }, [startTime, enrichmentStats]);

    const progressPercent = enrichmentStats.total > 0
        ? Math.round(((enrichmentStats.completed + enrichmentStats.failed) / enrichmentStats.total) * 100)
        : 0;

    const pending = enrichmentStats.total - enrichmentStats.completed - enrichmentStats.failed;

    if (!visible) return null;

    // ═══════════════════════════════════════════════════════════
    // FINISHED STATE (Completed or Stopped)
    // ═══════════════════════════════════════════════════════════
    if (isFinished) {
        return (
            <div className="processing-overlay">
                <div className="po-backdrop" />
                <div className="po-bg-effects"><div className="po-grid" /></div>
                <div className="po-content po-content--finished">
                    <div className="po-finish-icon-wrap">
                        {pipelineState === 'completed' ? (
                            <div className="po-finish-icon po-finish-icon--success"><Trophy className="w-12 h-12" /></div>
                        ) : (
                            <div className="po-finish-icon po-finish-icon--stopped"><AlertTriangle className="w-12 h-12" /></div>
                        )}
                    </div>
                    <h2 className="po-finish-title">
                        {pipelineState === 'completed' ? 'Pipeline Complete!' : 'Pipeline Stopped'}
                    </h2>
                    <p className="po-finish-subtitle">
                        {pipelineState === 'completed'
                            ? 'All records have been processed successfully.'
                            : 'The pipeline was stopped. Completed records have been preserved.'}
                    </p>
                    <div className="po-stats-grid po-stats-grid--finish">
                        <div className="po-stat-card po-stat-card--progress">
                            <div className="po-stat-icon"><Zap className="w-5 h-5" /></div>
                            <div className="po-stat-info">
                                <div className="po-stat-label">Processed</div>
                                <div className="po-stat-value">
                                    {enrichmentStats.completed + enrichmentStats.failed}
                                    <span className="po-stat-dim"> / {enrichmentStats.total}</span>
                                </div>
                            </div>
                        </div>
                        <div className="po-stat-card po-stat-card--eta">
                            <div className="po-stat-icon"><Clock className="w-5 h-5" /></div>
                            <div className="po-stat-info">
                                <div className="po-stat-label">Duration</div>
                                <div className="po-stat-value">{elapsed}</div>
                            </div>
                        </div>
                        <div className="po-stat-card po-stat-card--success">
                            <div className="po-stat-icon"><CheckCircle className="w-5 h-5" /></div>
                            <div className="po-stat-info">
                                <div className="po-stat-label">Success</div>
                                <div className="po-stat-value">{enrichmentStats.completed}</div>
                            </div>
                        </div>
                        <div className="po-stat-card po-stat-card--failed">
                            <div className="po-stat-icon"><XCircle className="w-5 h-5" /></div>
                            <div className="po-stat-info">
                                <div className="po-stat-label">Failed</div>
                                <div className="po-stat-value">{enrichmentStats.failed}</div>
                            </div>
                        </div>
                    </div>
                    <div className="po-progress-wrap">
                        <div className="po-progress-track">
                            <div className="po-progress-fill" style={{
                                width: `${progressPercent}%`,
                                background: pipelineState === 'completed'
                                    ? 'linear-gradient(90deg, #0f6cbd, #2886c8, #75b6e7)'
                                    : 'linear-gradient(90deg, #f59e0b, #eab308)',
                            }}><div className="po-progress-glow" /></div>
                        </div>
                        <div className="po-progress-labels">
                            <span>{progressPercent}% Complete</span>
                            <span>{enrichmentStats.completed} Success · {enrichmentStats.failed} Failed</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="po-btn po-btn--close-finish">
                        <X className="w-4 h-4" />
                        {pipelineState === 'completed' ? 'View Results' : 'Close'}
                    </button>
                    {enrichmentLogs.length > 0 && (
                        <details className="po-console-details">
                            <summary className="po-console-summary">
                                <Terminal className="w-3.5 h-3.5" /> View Console Log ({enrichmentLogs.length} entries)
                            </summary>
                            <div className="po-console">
                                <div className="po-console-body" ref={overlayConsoleRef}>
                                    {enrichmentLogs.slice(-50).map((log, idx) => (
                                        <div key={idx} className={`po-log-line po-log--${log.type || 'info'}`}>
                                            <span className="po-log-time">[{log.timestamp}]</span>
                                            {log.message}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </details>
                    )}
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════
    // ACTIVE STATE (Running or Paused)
    // ═══════════════════════════════════════════════════════════
    return (
        <div className="processing-overlay" style={{ '--progress': `${progressPercent}%` }}>
            <div className="po-backdrop" />

            {/* Background effects */}
            <div className="po-bg-effects">
                <div className="po-grid" />
                <div className="po-scanline" style={{ animationPlayState: pipelineState === 'paused' ? 'paused' : 'running' }} />
                <div className="po-particles" key={particleKey}>
                    {Array.from({ length: 24 }, (_, i) => (
                        <div key={i} className="po-particle" style={{
                            '--x': `${Math.random() * 100}%`,
                            '--y': `${Math.random() * 100}%`,
                            '--dx': `${(Math.random() - 0.5) * 300}px`,
                            '--dy': `${(Math.random() - 0.5) * 300}px`,
                            '--delay': `${Math.random() * 5}s`,
                            '--dur': `${4 + Math.random() * 6}s`,
                            '--size': `${2 + Math.random() * 4}px`,
                            animationPlayState: pipelineState === 'paused' ? 'paused' : 'running',
                        }} />
                    ))}
                </div>
                <svg className="po-network" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg">
                    {Array.from({ length: 8 }, (_, i) => {
                        const x1 = 100 + Math.random() * 1000, y1 = 100 + Math.random() * 600;
                        const x2 = 100 + Math.random() * 1000, y2 = 100 + Math.random() * 600;
                        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} className="po-net-line" style={{ '--delay': `${i * 0.8}s` }} />;
                    })}
                    {Array.from({ length: 12 }, (_, i) => (
                        <circle key={`n${i}`} cx={100 + Math.random() * 1000} cy={100 + Math.random() * 600} r="3" className="po-net-node" style={{ '--delay': `${i * 0.5}s` }} />
                    ))}
                </svg>
            </div>

            {/* Main content */}
            <div className="po-content">
                <div className="po-run-header">
                    <div className={`po-run-state ${pipelineState === 'paused' ? 'po-run-state--paused' : ''}`}>
                        <span className="po-run-state-dot" />
                        {pipelineState === 'paused' ? 'PAUSED' : 'RUNNING'}
                    </div>
                    <div className="po-run-heading">
                        <h2>Enrichment Pipeline</h2>
                        <p>
                            Processing record <strong>{Math.max(1, enrichmentStats.current || 1)}</strong> of <strong>{enrichmentStats.total}</strong>
                        </p>
                    </div>
                    <div className="po-run-percent">{progressPercent}%</div>
                </div>

                <div className="po-run-layout">
                    <section className="po-run-left" aria-label="Pipeline status and controls">
                {/* Animated Bot */}
                <div className="po-bot-area">
                    <div className={`po-bot ${pipelineState === 'paused' ? 'po-bot--paused' : ''}`}>
                        <div className="po-bot-body">
                            <div className="po-bot-antenna"><div className="po-bot-antenna-ball" /></div>
                            <div className="po-bot-head">
                                <div className="po-bot-eye po-bot-eye--left" />
                                <div className="po-bot-eye po-bot-eye--right" />
                                <div className="po-bot-mouth" />
                            </div>
                            <div className="po-bot-torso">
                                <div className="po-bot-screen">{pipelineState === 'paused' ? '⏸' : '⚡'}</div>
                            </div>
                            <div className="po-bot-arms">
                                <div className="po-bot-arm po-bot-arm--left"><Search className="w-3 h-3" /></div>
                                <div className="po-bot-arm po-bot-arm--right"><Globe className="w-3 h-3" /></div>
                            </div>
                        </div>
                        <div className="po-bot-beam" />
                        <div className="po-data-collect">
                            {Array.from({ length: 6 }, (_, i) => (
                                <div key={i} className="po-data-frag" style={{ '--i': i, animationPlayState: pipelineState === 'paused' ? 'paused' : 'running' }}>
                                    {['{ }', '< />', '01', 'DB', '⚙', '📄'][i]}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="po-bot-status">
                        {pipelineState === 'paused' ? (
                            <span className="po-status-paused">Pipeline Paused</span>
                        ) : (
                            <span className="po-status-active">
                                {previewMode
                                    ? (currentStepName || `Processing Record ${enrichmentStats.current}`)
                                    : (PIPELINE_STEPS[activeStepIdx]?.label || `Processing Record ${enrichmentStats.current}`)
                                }{dots}
                            </span>
                        )}
                    </div>
                </div>

                {/* Stats */}
                <div className="po-stats-grid po-stats-grid--running">
                    <div className="po-stat-card po-stat-card--progress">
                        <div className="po-stat-icon"><Zap className="w-5 h-5" /></div>
                        <div className="po-stat-info">
                            <div className="po-stat-label">Progress</div>
                            <div className="po-stat-value">
                                {enrichmentStats.completed + enrichmentStats.failed}
                                <span className="po-stat-dim"> / {enrichmentStats.total}</span>
                            </div>
                        </div>
                        <div className="po-stat-badge">{progressPercent}%</div>
                    </div>
                    <div className="po-stat-card po-stat-card--eta">
                        <div className="po-stat-icon"><Clock className="w-5 h-5" /></div>
                        <div className="po-stat-info">
                            <div className="po-stat-label">ETA</div>
                            <div className="po-stat-value">{eta || '—'}</div>
                        </div>
                    </div>
                    <div className="po-stat-card po-stat-card--success">
                        <div className="po-stat-icon"><CheckCircle className="w-5 h-5" /></div>
                        <div className="po-stat-info">
                            <div className="po-stat-label">Success</div>
                            <div className="po-stat-value">{enrichmentStats.completed}</div>
                        </div>
                    </div>
                    <div className="po-stat-card po-stat-card--pending">
                        <div className="po-stat-icon"><Database className="w-5 h-5" /></div>
                        <div className="po-stat-info">
                            <div className="po-stat-label">Pending</div>
                            <div className="po-stat-value">{pending}</div>
                        </div>
                    </div>
                    <div className="po-stat-card po-stat-card--failed">
                        <div className="po-stat-icon"><XCircle className="w-5 h-5" /></div>
                        <div className="po-stat-info">
                            <div className="po-stat-label">Failed</div>
                            <div className="po-stat-value">{enrichmentStats.failed}</div>
                        </div>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="po-progress-wrap">
                    <div className="po-progress-track">
                        <div className="po-progress-fill" style={{
                            width: `${progressPercent}%`,
                            background: pipelineState === 'paused'
                                ? 'linear-gradient(90deg, #f59e0b, #eab308)'
                                : 'linear-gradient(90deg, #0f6cbd, #2886c8, #75b6e7)',
                        }}><div className="po-progress-glow" /></div>
                    </div>
                    <div className="po-progress-labels">
                        <span>Record {enrichmentStats.current} of {enrichmentStats.total}</span>
                        <span>{progressPercent}% Complete</span>
                    </div>
                </div>

                {/* Controls */}
                <div className="po-controls">
                    {pipelineState === 'running' ? (
                        <button onClick={onPause} className="po-btn po-btn--pause">
                            <Pause className="w-4 h-4" /> Pause
                        </button>
                    ) : (
                        <button onClick={onResume} className="po-btn po-btn--resume">
                            <PlayCircle className="w-4 h-4" /> Resume
                        </button>
                    )}
                    <button onClick={onSkip} className="po-btn po-btn--skip">
                        <SkipForward className="w-4 h-4" /> Skip Record
                    </button>
                    <button onClick={onStop} className="po-btn po-btn--stop">
                        <StopCircle className="w-4 h-4" /> Stop Pipeline
                    </button>
                </div>

                    </section>
                    <section className="po-run-right" aria-label="Live pipeline steps">

                {/* Step Review Panel (only when Preview ON and stepReview is set) */}
                {stepReview && (
                    <div className="po-review-panel">
                        <div className="po-review-header">
                            <div className="po-review-step-badge">{stepReview.stepNumber}</div>
                            <div>
                                <div className="po-review-title">{stepReview.stepName}</div>
                                <div className="po-review-subtitle">
                                    Row {stepReview.row.rowNum} — {stepReview.row.identifier}
                                </div>
                            </div>
                        </div>
                        <div className="po-review-body">
                            {renderStepData && renderStepData()}
                        </div>
                        <div className="po-review-actions">
                            <button onClick={handleApprove} className="po-btn po-btn--approve">
                                <CheckCircle className="w-4 h-4" /> Approve & Continue
                            </button>
                            <button onClick={handleSkipRow} className="po-btn po-btn--skip-row">
                                <SkipForward className="w-4 h-4" /> Skip Row
                            </button>
                        </div>
                    </div>
                )}

                {/* ═══ Console / Activity Feed ═══ */}
                {previewMode ? (
                    /* ── PREVIEW ON: Show detailed console log ── */
                    enrichmentLogs.length > 0 && (
                        <div className="po-console">
                            <div className="po-console-header">
                                <Terminal className="w-3.5 h-3.5" />
                                <span>Pipeline Console</span>
                                <div className="po-console-live">
                                    {pipelineState === 'running' && <><div className="po-live-dot" /> LIVE</>}
                                </div>
                            </div>
                            <div className="po-console-body" ref={overlayConsoleRef}>
                                {enrichmentLogs.slice(-40).map((log, idx) => {
                                    const colorMap = {
                                        header: 'po-log--header', step: 'po-log--step',
                                        success: 'po-log--success', error: 'po-log--error',
                                        info: 'po-log--info', detail: 'po-log--detail',
                                        warning: 'po-log--warning', divider: 'po-log--divider',
                                    };
                                    return (
                                        <div key={idx} className={`po-log-line ${colorMap[log.type] || 'po-log--info'}`}>
                                            <span className="po-log-time">[{log.timestamp}]</span>
                                            {log.message}
                                        </div>
                                    );
                                })}
                                <div ref={consoleEndRef} />
                            </div>
                        </div>
                    )
                ) : (
                    /* ── PREVIEW OFF: Show generic pipeline step tracker ── */
                    <div className="po-activity">
                        <div className="po-activity-header">
                            <Workflow className="w-3.5 h-3.5" />
                            <span>Pipeline Activity</span>
                            <div className="po-console-live">
                                {pipelineState === 'running' && <><div className="po-live-dot" /> LIVE</>}
                            </div>
                        </div>
                        <div className="po-activity-body">
                            {PIPELINE_STEPS.map((step, idx) => {
                                const isDone = idx < activeStepIdx;
                                const isActive = idx === activeStepIdx && pipelineState === 'running';
                                const isPending = idx > activeStepIdx;

                                return (
                                    <div key={step.id}
                                        className={`po-activity-step ${isDone ? 'po-activity-step--done' : ''} ${isActive ? 'po-activity-step--active' : ''} ${isPending ? 'po-activity-step--pending' : ''}`}
                                    >
                                        <div className="po-activity-indicator">
                                            {isDone ? (
                                                <div className="po-activity-check"><CheckCircle className="w-3.5 h-3.5" /></div>
                                            ) : isActive ? (
                                                <div className="po-activity-pulse" />
                                            ) : (
                                                <div className="po-activity-dot" />
                                            )}
                                            {idx < PIPELINE_STEPS.length - 1 && <div className={`po-activity-line ${isDone ? 'po-activity-line--done' : ''}`} />}
                                        </div>
                                        <div className="po-activity-content">
                                            <div className="po-activity-icon">{step.icon}</div>
                                            <div className="po-activity-text">
                                                <div className="po-activity-label">
                                                    {step.label}
                                                    {isActive && <span className="po-activity-dots">{dots}</span>}
                                                </div>
                                                <div className="po-activity-desc">{step.desc}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                    </section>
                </div>
            </div>
        </div>
    );
}
